export const GREAT_CIRCLE_SEGMENTS = 96

/**
 * Interpolate points along the great-circle (shortest) path between two
 * coordinates using spherical linear interpolation, so the route curves like
 * the native MKMapView geodesic instead of a straight rhumb line.
 */
export function greatCirclePoints(
  fromLat: number,
  fromLon: number,
  toLat: number,
  toLon: number,
  segments = GREAT_CIRCLE_SEGMENTS,
): Array<{ lat: number; lon: number }> {
  const toRad = (d: number) => (d * Math.PI) / 180
  const toDeg = (r: number) => (r * 180) / Math.PI

  const φ1 = toRad(fromLat)
  const λ1 = toRad(fromLon)
  const φ2 = toRad(toLat)
  const λ2 = toRad(toLon)

  // Angular distance between the two points.
  const d =
    2 *
    Math.asin(
      Math.sqrt(
        Math.sin((φ2 - φ1) / 2) ** 2 +
          Math.cos(φ1) * Math.cos(φ2) * Math.sin((λ2 - λ1) / 2) ** 2,
      ),
    )

  if (d === 0) return [{ lat: fromLat, lon: fromLon }]

  const points: Array<{ lat: number; lon: number }> = []
  for (let i = 0; i <= segments; i++) {
    const f = i / segments
    const A = Math.sin((1 - f) * d) / Math.sin(d)
    const B = Math.sin(f * d) / Math.sin(d)
    const x = A * Math.cos(φ1) * Math.cos(λ1) + B * Math.cos(φ2) * Math.cos(λ2)
    const y = A * Math.cos(φ1) * Math.sin(λ1) + B * Math.cos(φ2) * Math.sin(λ2)
    const z = A * Math.sin(φ1) + B * Math.sin(φ2)
    const lat = Math.atan2(z, Math.sqrt(x * x + y * y))
    let lon = toDeg(Math.atan2(y, x))
    // Map renderers draw segments along the literal longitude path, so keep
    // longitudes continuous across the antimeridian (e.g. 179 → 181, not
    // 179 → -179) or the polyline snaps back across the whole map.
    const prev = points[points.length - 1]
    if (prev) lon += Math.round((prev.lon - lon) / 360) * 360
    points.push({ lat: toDeg(lat), lon })
  }
  return points
}

/**
 * Fraction of the flight elapsed right now, clamped to [0, 1]. Falls back to
 * the route midpoint when either timestamp is missing or invalid — same
 * behavior as the native plugin's `progress(departure:arrival:)`.
 */
export function flightProgress(
  departure?: Date | string,
  arrival?: Date | string,
): number {
  if (!departure || !arrival) return 0.5
  const dep = new Date(departure).getTime()
  const arr = new Date(arrival).getTime()
  if (!Number.isFinite(dep) || !Number.isFinite(arr) || arr <= dep) return 0.5
  return Math.min(1, Math.max(0, (Date.now() - dep) / (arr - dep)))
}

/** Initial bearing (degrees clockwise from north) from one point to the next. */
export function bearing(
  from: { lat: number; lon: number },
  to: { lat: number; lon: number },
): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const lat1 = toRad(from.lat)
  const lat2 = toRad(to.lat)
  const dLon = toRad(to.lon - from.lon)
  const y = Math.sin(dLon) * Math.cos(lat2)
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon)
  return ((Math.atan2(y, x) * 180) / Math.PI) % 360
}

/** Re-normalize a (possibly unwrapped) longitude back into [-180, 180]. */
export function normalizeLon(lon: number): number {
  return ((((lon + 540) % 360) + 360) % 360) - 180
}
